// TypeScript Print Manager with Enhanced Functionality

interface PrintOptions {
    title?: string;
    selector?: string;
    windowFeatures?: string;
    enableKeyboardShortcut?: boolean;
}

interface PrintWindow extends Window {
    document: Document;
}

// Constants for better maintainability
const PRINT_CONSTANTS = {
    PRINT_DELAY: 250,
    DEFAULT_WINDOW_FEATURES: 'width=800,height=600',
    DEFAULT_TITLE: ' ',
    DEFAULT_SELECTOR: '.indexResultContainer',
    KEYBOARD_SHORTCUT_ENABLED: true
} as const;

// Extracted CSS styles for better organization
const getPrintStyles = (): string => `
  <style>
    @media print {
     * {
         -webkit-print-color-adjust: exact !important;
         color-adjust: exact !important;
         box-shadow: none !important;
         text-shadow: none !important;
     }
      @page {
         margin: 1cm;
         size: A4;
      }

      body {
         font-family: Roboto, Arial, sans-serif;
         font-size: 10pt;
         line-height: 1.3;
         margin: 0;
         padding: 0;
      }

      /* Hide elements that shouldn't be printed */
      .printPDFbtn,
      .school-info-bubble,
      .fa-info-circle,
      [data-toggle="school-profile-tooltip"],
      .autocomplete-input-container,
      .autocomplete-results, .schoolNameBlock {
         display: none !important;
      }

      /* Layout adjustments for print */
       .resultContainer {
        display: block !important;
        flex-direction: column !important;
        padding-top: 0 !important;
      }

      .resultContainer .left-side,
        .resultContainer .right-side {
        width: 100% !important;
        padding-right: 0 !important;
        float: none !important;
        margin-left: 0 !important;
      }

      .resultContainer .left-side {
        margin-bottom: 0 !important;
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 15pt !important; 
      }

      /* School name styling */
      .schoolName {
         background: #061a53 !important;
         color: #ffffff !important;
         padding: 10pt !important;
         text-align: center !important;
         border: 1pt solid #1D7686 !important;
         margin-bottom: 15pt !important;
         page-break-inside: avoid;
      }

      .schoolName h1, .schoolName h2, .schoolName h3, .schoolName h4 {
         color: #ffffff !important;
         margin: 0 !important;
      }

      /* Opportunity block styling */
      .opportunity-block {
         margin-bottom: 10pt !important;
         page-break-inside: avoid;
         border: 1pt solid #CECECE !important;
      }

      .opportunity-block .promo-inner {
         padding: 10pt !important;
      }

      .opportunity-block h4 {
         font-size: 12pt !important;
         margin-bottom: 10pt !important;
         font-weight: bold !important;
      }

      /* Chart styling */
      .opportunity-chart h4 {
         text-align: center !important;
        margin: 0 !important;
      }

      .opportunity-chart-value {
         font-size: 25pt !important;
         font-weight: bold !important;
         text-align: center !important;
         margin: 5pt 0 !important;
      }

      .opportunity-chart-bar {
         margin: 5pt 0 !important;
      }

      .opportunity-chart-bar-block {
         padding: 0 8pt !important;
      }

      .opportunity-chart-bar-bg {
         height: 15pt !important;
         background: linear-gradient(to right, #EAF5F5 0%, #2D8494 100%) !important;
         border: 1pt solid #CECECE !important;
         position: relative !important;
      }

      .opportunity-block.avarage-block .opportunity-chart-bar-bg {
         background: linear-gradient(to right, #EAF5F5 0%, #FFA110 100%) !important;
      }

      .opportunity-chart-bar-average {
         position: absolute !important;
         top: -8pt !important;
      }

      .opportunity-chart-bar-average:before {
         content: "";
         display: block !important;
         width: 20px !important;
         height: 16px !important;
         background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 20' width='40' height='20'%3E%3Cpolygon points='0,0 40,0 20,20' fill='%23000000'/%3E%3C/svg%3E") !important;
         background-repeat: no-repeat !important;
         background-size: contain !important;
      }

      .opportunity-chart-bar-note {
         display: flex !important;
         justify-content: space-between !important;
         font-size: 9pt !important;
         padding: 0 15pt !important:
      }

      .opportunity-chart-bar-note > div {
         text-align: center !important;
      }

      .opportunity-chart-bar-note > div:before {
         content: "";
         display: inline-block !important;
         width: 1px !important;
         height: 10px !important;
         background: #000 !important;
      }

      .opportunity-chart-note {
         text-align: center !important;
         margin: 0 !important;
         font-weight: bold !important;
         font-size: 10pt !important;
      }

      /* Card info styling */
      .card-info-block {
         margin-bottom: 10pt !important;
         margin-left: 0 !important;
         page-break-inside: avoid;
         border: 1pt solid #d4d4d4 !important;
      }

      .card-info-title {
         padding: 8pt 10pt !important;
         border-bottom: 1pt solid #d4d4d4 !important;
      }

      .card-info-title.blue {
         background: #1d48c0 !important;
         color: #ffffff !important;
      }

      .card-info-title.purple {
         background: #8b2ba1 !important;
         color: #ffffff !important;
      }

      .card-info-title.green {
         background: #047a6e !important;
         color: #ffffff !important;
      }

      .card-info-title h4 {
         margin: 0 !important;
         font-size: 11pt !important;
         font-weight: bold !important;
      }

      .card-info-content {
         display: grid !important;
         grid-template-columns: 50% 50% !important;
         padding: 10pt 0 !important;
      }

      .content-item {
         border-right: 1px solid #d4d4d4 !important;
         padding: 0 10pt 15pt !important;
         display: flex !important;
         flex-direction: row !important
      }
        .content-item:nth-last-child(-n + 2) {
             padding-bottom: 0;
        }

      .content-item:last-child {
         border-bottom: none !important;
      }

      .content-item-desc {
         font-size: 15pt !important;
        font-weight: 400!important;
      }
     .content-item-desc .content-item-title {
        font-size: 15pt!important;
        font-weight: bold!important;
      }

      /* Badge styling for different card types */
      .card-info-title + .card-info-content .badge {
         color: #000000 !important;
         padding: 3pt 10pt !important;
         border: none !important;
         border-radius: 5px !important;
         font-weight: bold !important;
         margin-right: 15pt !important;
         font-size: 15pt !important;
         display: block !important;
 
      }
       .card-info-title + .card-info-content .badge .from {
            font-size: 25pt !important;
       }

      .card-info-title.blue + .card-info-content .badge {
         background: #efefef !important;
      }
      .card-info-title.blue + .card-info-content .badge .from {
         color: #1d48c0 !important;
       }

      .card-info-title.purple + .card-info-content .badge {
         background: #ead9ee !important;
      }
     .card-info-title.purple + .card-info-content .badge .from {
        color: #8b2ba1 !important;
      }

      .card-info-title.green + .card-info-content .badge {
         background: #dbf0e7 !important;
      }
        .card-info-title.green + .card-info-content .badge .from {
          color: #047a6e !important;
        }

      .content-item p {
         margin: 0 !important;
      }

      /* Info block styling */
      .info-block {
         background: #f8f9fa !important;
         border: 1pt solid #dee2e6 !important;
         padding: 10pt !important;
         margin-bottom: 10pt !important;
         page-break-inside: avoid;
      }

      /* Page break rules */
      h1, h2, h3, h4, h5, h6 {
         page-break-after: avoid;
      }

      .opportunity-block,
        .card-info-block,
        .info-block {
        page-break-inside: avoid;
        }

        /* Remove shadows and effects for print */
        * {
        box-shadow: none !important;
        text-shadow: none !important;
      }
        .title-block h3.opportunty-block-title {
            display: none;
        }
    }
  </style>
`;

/**
 * Core function for printing PDF with custom styles
 * @param options - Print configuration options
 */
function printToPDF(options: PrintOptions = {}): void {
    const {
        title = PRINT_CONSTANTS.DEFAULT_TITLE,
        selector = PRINT_CONSTANTS.DEFAULT_SELECTOR,
        windowFeatures = PRINT_CONSTANTS.DEFAULT_WINDOW_FEATURES
    } = options;

    // Create new window for printing
    const printWindow: PrintWindow | null = window.open(
        '',
        '_blank',
        windowFeatures
    ) as PrintWindow;

    if (!printWindow) {
        console.error('Failed to open print window');
        alert('Failed to open print window. Please check your browser settings.');
        return;
    }

    // Get content for printing
    const printContent: Element | null = document.querySelector(selector);
    console.log('printContent: ', printContent, selector)
    if (!printContent) {
        console.error('Print content not found for selector:', selector);
        alert('Print content not found. Please ensure the content is loaded.');
        printWindow.close();
        return;
    }

    // Format HTML for printing
    const printHTML: string = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      ${getPrintStyles()}
    </head>
    <body>
      ${printContent.outerHTML}
    </body>
    </html>
  `;

    try {
        // Write HTML to new window
        printWindow.document.write(printHTML);
        printWindow.document.close();

        // Wait for loading and start printing
        printWindow.onload = (): void => {
            setTimeout(() => {
                try {
                    printWindow.print();
                } catch (error) {
                    console.error('Print failed:', error);
                } finally {
                    printWindow.close();
                }
            }, PRINT_CONSTANTS.PRINT_DELAY);
        };

        // Handle potential errors
        printWindow.onerror = (error): void => {
            console.error('Print window error:', error);
            printWindow.close();
        };
    } catch (error) {
        console.error('Failed to prepare print window:', error);
        printWindow.close();
        alert('Failed to prepare print document. Please try again.');
    }
}

/**
 * Enhanced Print Manager with keyboard shortcut support
 */
export class PrintManager {
    private defaultOptions: PrintOptions;
    private keyboardShortcutEnabled: boolean;
    private boundKeyHandler: (e: KeyboardEvent) => void;

    constructor(options: Partial<PrintOptions> = {}) {
        this.defaultOptions = {
            title: PRINT_CONSTANTS.DEFAULT_TITLE,
            selector: PRINT_CONSTANTS.DEFAULT_SELECTOR,
            windowFeatures: PRINT_CONSTANTS.DEFAULT_WINDOW_FEATURES,
            enableKeyboardShortcut: PRINT_CONSTANTS.KEYBOARD_SHORTCUT_ENABLED,
            ...options
        };

        this.keyboardShortcutEnabled = this.defaultOptions.enableKeyboardShortcut ?? true;
        this.boundKeyHandler = this.handleKeyboardShortcut.bind(this);
        this.init();
    }

    /**
     * Initialize the print manager
     */
    private init(): void {
        this.attachEventListeners();

        if (this.keyboardShortcutEnabled) {
            this.enableKeyboardShortcut();
        }
    }

    /**
     * Attach click event listeners for print buttons
     */
    private attachEventListeners(): void {
        // Use event delegation for dynamic elements
        document.addEventListener('click', this.handleButtonClick.bind(this));

        // Additional binding for elements loaded after DOM ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', this.bindDirectListeners.bind(this));
        } else {
            this.bindDirectListeners();
        }
    }

    /**
     * Handle button click events
     */
    private handleButtonClick(e: Event): void {
        const target = e.target as HTMLElement;

        if (target.classList.contains('printPDFbtn') || target.closest('.printPDFbtn')) {
            e.preventDefault();
            e.stopPropagation();
            this.print();
        }
    }

    /**
     * Bind direct event listeners to existing print buttons
     */
    private bindDirectListeners(): void {
        const printButtons: NodeListOf<HTMLElement> = document.querySelectorAll('.printPDFbtn');

        printButtons.forEach((button: HTMLElement): void => {
            // Remove existing listener to avoid duplicates
            button.removeEventListener('click', this.handleDirectButtonClick.bind(this));
            button.addEventListener('click', this.handleDirectButtonClick.bind(this));
        });
    }

    /**
     * Handle direct button click events
     */
    private handleDirectButtonClick(e: Event): void {
        e.preventDefault();
        e.stopPropagation();
        this.print();
    }

    /**
     * Enable keyboard shortcut (Ctrl+P / Cmd+P)
     */
    private enableKeyboardShortcut(): void {
        document.addEventListener('keydown', this.boundKeyHandler);
    }

    /**
     * Disable keyboard shortcut
     */
    public disableKeyboardShortcut(): void {
        document.removeEventListener('keydown', this.boundKeyHandler);
        this.keyboardShortcutEnabled = false;
    }

    /**
     * Handle keyboard shortcut events
     */
    private handleKeyboardShortcut(e: KeyboardEvent): void {
        // Check for Ctrl+P (Windows/Linux) or Cmd+P (Mac)
        if (e.key === 'p' && (e.ctrlKey || e.metaKey)) {
            // Only intercept if we're in a context where our custom print should be used
            const printContent = document.querySelector(this.defaultOptions.selector!);

            if (printContent) {
                e.preventDefault();
                e.stopPropagation();
                this.print();
            }
        }
    }

    /**
     * Execute print with optional custom options
     */
    public print(options: Partial<PrintOptions> = {}): void {
        const mergedOptions: PrintOptions = { ...this.defaultOptions, ...options };
        printToPDF(mergedOptions);
    }

    /**
     * Update default options
     */
    public updateOptions(options: Partial<PrintOptions>): void {
        this.defaultOptions = { ...this.defaultOptions, ...options };
    }

    /**
     * Clean up event listeners when destroying the instance
     */
    public destroy(): void {
        document.removeEventListener('click', this.handleButtonClick.bind(this));

        if (this.keyboardShortcutEnabled) {
            this.disableKeyboardShortcut();
        }

        // Remove direct listeners
        const printButtons: NodeListOf<HTMLElement> = document.querySelectorAll('.printPDFbtn');
        printButtons.forEach((button: HTMLElement): void => {
            button.removeEventListener('click', this.handleDirectButtonClick.bind(this));
        });
    }
}